import { useEffect } from 'react';
import type { ColumnValue } from '../worker/protocol';

export interface InspectedCell {
  column: string;
  rowIndex: number;
  value: ColumnValue;
}

interface CellInspectorProps {
  cell: InspectedCell | null;
  onClose: () => void;
}

function describeType(v: ColumnValue): string {
  if (v === null || v === undefined) return 'null';
  if (typeof v === 'number') return Number.isInteger(v) ? 'INTEGER' : 'REAL';
  if (typeof v === 'boolean') return 'boolean';
  if (v instanceof Uint8Array) return `BLOB (${v.byteLength} bytes)`;
  return 'TEXT';
}

function hexDump(bytes: Uint8Array, max = 256): string {
  const slice = bytes.subarray(0, Math.min(bytes.byteLength, max));
  const lines: string[] = [];
  for (let i = 0; i < slice.byteLength; i += 16) {
    const row = slice.subarray(i, i + 16);
    const hex = Array.from(row, (b) => b.toString(16).padStart(2, '0')).join(' ');
    const ascii = Array.from(row, (b) => (b >= 32 && b < 127 ? String.fromCharCode(b) : '.')).join(
      '',
    );
    lines.push(`${i.toString(16).padStart(4, '0')}  ${hex.padEnd(48)}  ${ascii}`);
  }
  if (bytes.byteLength > max) {
    lines.push(`… (${bytes.byteLength - max} more bytes)`);
  }
  return lines.join('\n');
}

function renderValue(value: ColumnValue): { display: string; mono: boolean } {
  if (value === null || value === undefined) return { display: 'null', mono: false };
  if (value instanceof Uint8Array) return { display: hexDump(value), mono: true };
  if (typeof value === 'boolean') return { display: String(value), mono: true };
  return { display: String(value), mono: typeof value !== 'number' ? false : true };
}

export function CellInspector({ cell, onClose }: CellInspectorProps) {
  // External system: keyboard shortcut to dismiss. Escape is the universal
  // "close transient panel" key in JetBrains tools.
  useEffect(() => {
    if (!cell) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [cell, onClose]);

  if (!cell) return null;

  const { display, mono } = renderValue(cell.value);
  const length = typeof cell.value === 'string' ? cell.value.length : null;

  const copyValue = async () => {
    const text =
      cell.value == null
        ? ''
        : cell.value instanceof Uint8Array
          ? `<BLOB ${cell.value.byteLength} bytes>`
          : String(cell.value);
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Clipboard API unavailable — silently no-op; the user can select+copy
      // from the panel.
    }
  };

  return (
    <aside className="flex h-full w-80 shrink-0 flex-col border-l border-[#323232] bg-[#3c3f41]">
      <div className="flex shrink-0 items-center justify-between border-b border-[#323232] bg-[#3c3f41] px-2 py-1">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-[#808080]">
          Cell Inspector
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={copyValue}
            className="rounded px-1.5 py-0.5 text-[11px] text-[#bbbbbb] hover:bg-[#4c5052] hover:text-[#e8e8e8]"
            title="Copy value"
          >
            Copy
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded px-1.5 py-0.5 text-[14px] leading-none text-[#bbbbbb] hover:bg-[#4c5052] hover:text-[#e8e8e8]"
            aria-label="Close inspector"
            title="Close (Esc)"
          >
            ×
          </button>
        </div>
      </div>
      <div className="border-b border-[#323232] bg-[#2b2b2b] px-3 py-2">
        <div className="font-mono text-[12px] text-[#e8e8e8]">{cell.column}</div>
        <div className="mt-1 flex items-center gap-2 text-[10px] text-[#808080]">
          <span className="rounded bg-[#3c3f41] px-1.5 py-0.5 text-[#6a8759]">
            {describeType(cell.value)}
          </span>
          <span>row {cell.rowIndex + 1}</span>
          {length != null && <span>· {length.toLocaleString()} chars</span>}
        </div>
      </div>
      <div className="flex-1 overflow-auto bg-[#2b2b2b] p-3">
        {cell.value === null || cell.value === undefined ? (
          <div className="italic text-[#606366]">null</div>
        ) : (
          <pre
            className={
              'whitespace-pre-wrap break-words text-[12px] leading-snug text-[#a9b7c6] ' +
              (mono ? 'font-mono' : 'font-sans')
            }
          >
            {display}
          </pre>
        )}
      </div>
    </aside>
  );
}
